/**
 * Desktop BACKLOG (DESIGN-CHANGELOG 2026-09-03 "BACKLOG (6d)" + the same-day
 * "BACKLOG tiles are Discover tiles" / "BACKLOG carries the standard screen
 * search + filter" revisions; `design/GT Mine.dc.html`, the `isBacklog` block).
 * AA phase 2.
 *
 * Full-screen behind MY GAMES' first door: the page bar, then the page's own
 * first row — the stat line, ALL / PLAYED / OWNED / WISHLISTED / UNOWNED,
 * `+ TRACK A GAME` — then the search box (`bkQ`, `/` focuses it), its match
 * note and `+ FILTERS`, then a 168px auto-fill grid of 2:3 tiles.
 *
 * THE TABS ARE AN OWNERSHIP AXIS (Marcus, 2026-09-15) and replace nothing: the
 * SHELF / SCORED / WATCHING strip he removed on 2026-09-04 asked about the
 * shelf, these ask what Steam says about him. PLAYED is `steam_owned_apps`
 * playtime — his call when asked — so a game he played and never scored is here
 * and not on MY SCORES. WISHLISTED is a live `?op=wishlist` read cached for the
 * session, because nothing in the schema persists a wishlist; it sits between
 * OWNED and UNOWNED so the strip reads have it · want it · neither.
 *
 * THE TILE IS A DISCOVER TILE: same cover, same bottom-gradient caption, the
 * shell's glossy tilt + sheen hover (`useHoverTilt`, the hook rather than
 * `HoverTile` for the reason `ResultTile` gives — the sheen sits UNDER the
 * caption). What differs is what it carries: the score badge top-right IS the
 * score control; the ✕ top-left takes it off your shelf and ARMS first
 * (`SURE?` + the 5 s drain, `data-arm`), because a one-tap remove was the
 * design's first cut and was withdrawn the same day; EARLY ACCESS rides the
 * caption. The bell and the watch line join in phase 4.
 *
 * `⟳ PULL IN YOUR STEAM WISHLIST` sits beside TRACK A GAME, dressed as GOTY's
 * AUTO-FILL pill (Marcus, 2026-09-15: "make something here that matches the
 * GOTY autofill button" — built repo-side, ahead of the design).
 *
 * The dashed `+ TRACK A SOLO GAME` tile ends the ALL tab. A shelf narrowed
 * to nothing by the search or the filter SAYS so rather than looking empty.
 */

import { Bell } from '../../components/Bell.tsx';
import { CoverArt } from '../../components/CoverArt.tsx';
import { PixelButton } from '../../components/PixelButton.tsx';
import { SearchBox } from '../../components/SearchBox.tsx';
import { useArt, useQueueArt } from '../../components/useArt.ts';
import { useEnsureMyLibrary, useEnsureWishlist } from '../../components/useMyLibrary.ts';
import { useQd } from '../../store/index.ts';
import { backlogVM } from '../../store/selectors.ts';
import type { BacklogRow } from '../../store/selectors.ts';
import FilterBar from '../components/FilterBar.tsx';
import PageBar from '../components/PageBar.tsx';
import { useSearchInputRef } from '../searchHotkey.ts';
import { useHoverTilt } from '../useHoverTilt.ts';

const MONO = 'var(--gt-font-mono)';
const PIXEL = 'var(--gt-font-pixel)';

function BacklogTile({ s: row }: { s: BacklogRow }): JSX.Element {
  const st = useQd();
  const art = useArt(row.id + 'p', row.coverCands);
  const tilt = useHoverTilt('bk:' + row.id);

  return (
    <div
      ref={tilt.ref}
      {...tilt.handlers}
      onClick={() => st.openGame(row.id)}
      style={{
        position: 'relative',
        aspectRatio: '2/3',
        background: row.fall,
        overflow: 'hidden',
        border: '1px solid var(--gt-border)',
        cursor: 'pointer',
        ...tilt.rest,
      }}
    >
      <CoverArt style={{ position: 'absolute', inset: 0 }} fs={15} ini={row.ini} hasCover={art.hasCover} cover={art.cover} />
      <div
        style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg,transparent 38%,rgba(5,5,8,.97) 78%)' }}
      />
      <div ref={tilt.sheenRef} style={tilt.restSheen} />

      {/* ✕ — top-left, armed before it commits. `grid-area:1/1` keeps the box
          the width of the wider label so it never resizes when it flips. */}
      <div
        onClick={(ev) => {
          ev.stopPropagation();
          if (row.rmArmed) void st.soloRm(row.id);
          else st.bkArmSet(row.id);
        }}
        data-arm="bk"
        data-tip="TAKE IT OUT OF YOUR BACKLOG — YOURS ONLY"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: 4,
          display: 'grid',
          minWidth: 26,
          textAlign: 'center',
          padding: '7px 9px',
          background: 'var(--gt-ink)',
          color: row.rmClr,
          fontFamily: MONO,
          fontSize: 10,
          lineHeight: 1,
          cursor: 'pointer',
          overflow: 'hidden',
        }}
      >
        {row.rmArmed ? (
          <span
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: '100%',
              background: 'var(--acc2)',
              opacity: 0.22,
              animation: 'gt-armdrain 5s linear forwards',
            }}
          />
        ) : null}
        <span style={{ gridArea: '1/1', position: 'relative', visibility: row.rmArmed ? 'hidden' : 'visible' }}>✕</span>
        <span style={{ gridArea: '1/1', position: 'relative', visibility: row.rmArmed ? 'visible' : 'hidden' }}>SURE?</span>
      </div>

      {/* the score badge IS the score control */}
      <div
        onClick={(ev) => {
          ev.stopPropagation();
          st.openScore(row.id);
        }}
        data-tip={row.scTip}
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          zIndex: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          background: 'var(--gt-ink)',
          padding: '6px 8px',
          cursor: 'pointer',
        }}
      >
        <span style={{ fontFamily: PIXEL, fontSize: 12, color: row.scClr, display: 'flex', alignItems: 'center' }}>
          <span style={{ transform: 'translateY(0.13em)' }}>{row.scTxt}</span>
        </span>
        <span style={{ fontFamily: MONO, fontSize: 6, letterSpacing: '.12em', color: 'var(--gt-muted-2)' }}>{row.scLab}</span>
      </div>

      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2,
          padding: '11px 11px 10px',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        <div
          style={{
            fontWeight: 600,
            fontSize: 12.5,
            lineHeight: 1.2,
            color: '#fff',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {row.name}
        </div>
        {/* EARLY ACCESS rides the caption, not the art (same-day revision). */}
        {row.ea ? (
          <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.03em', color: 'var(--gt-info)' }}>EARLY ACCESS</div>
        ) : null}
        {/* The watch line and the bell (AA phase 4): what is armed, or NOTHING
            WATCHED in ghost; the bell is solid + count when something is. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ flex: 1, minWidth: 0, fontFamily: MONO, fontSize: 8.5, letterSpacing: '.05em', color: row.wClr, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {row.w}
          </span>
          <span
            onClick={(ev) => {
              ev.stopPropagation();
              st.wOpen(row.id);
            }}
            data-tip={row.bellTip}
            style={{ display: 'flex', alignItems: 'center', gap: 4, border: '1px solid ' + row.bellBd, color: row.bellClr, padding: '3px 6px', cursor: 'pointer', flex: 'none' }}
          >
            <Bell on={row.bellOn} size={11} color={row.bellClr} />
            {row.bellOn ? <span style={{ fontFamily: MONO, fontSize: 8.5 }}>{row.bellN}</span> : null}
          </span>
        </div>
      </div>
    </div>
  );
}

export default function Backlog(): JSX.Element {
  const s = useQd();
  const v = backlogVM(s);
  const bkRef = useSearchInputRef();

  // Same art fetch the phone shelf does, for the same reason — see the note in
  // `screens/Backlog.tsx`: a game tracked mid-session from the local catalog
  // is on no surface that fetches assets, and a modern appid has no art
  // without them.
  useQueueArt(v.bkRows.map((r) => r.id));

  // The ownership tabs' data — the whole shelf, not the visible rows. See the
  // note in `screens/Backlog.tsx`.
  useEnsureMyLibrary(v.bkAppids);
  useEnsureWishlist();

  return (
    <div data-screen-label="Backlog" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <PageBar title="BACKLOG" />

      {/* the page's own first row: the stat, the ownership tabs, the wishlist
          pill, TRACK A GAME */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px 24px 0', flexWrap: 'wrap' }}>
        <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.07em', color: 'var(--acc)', whiteSpace: 'nowrap' }}>
          {v.bkSub}
        </span>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', fontFamily: MONO, fontSize: 10 }}>
          {v.bkTabs.map((t) => (
            <div
              key={t.key}
              onClick={() => s.setBkTab(t.key)}
              style={{
                border: '1px solid ' + t.bd,
                color: t.clr,
                background: t.bg,
                padding: '8px 13px',
                marginLeft: -1,
                position: 'relative',
                zIndex: t.z,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {t.l}
            </div>
          ))}
        </div>
        {/* the Steam wishlist import — GOTY's AUTO-FILL pill, same dress */}
        <div
          onClick={() => void s.wishImport()}
          data-tip={v.wishSub}
          data-tip-pos="r"
          style={{
            border: '1px solid ' + v.wishBd,
            color: v.wishClr,
            fontFamily: MONO,
            fontSize: '10.5px',
            padding: '8px 12px',
            whiteSpace: 'nowrap',
            cursor: v.wishBusy ? 'progress' : 'pointer',
          }}
        >
          {v.wishLbl}
        </div>
        <PixelButton label="+ TRACK A GAME" onClick={() => s.openTrack()} fs={8} pad="10px 14px" nowrap fit />
      </div>

      {/* search · match note · + FILTERS ---------------------------------------- */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 24px 0', flexWrap: 'wrap' }}>
        <SearchBox
          value={v.bkQ}
          onChange={(e) => s.setBkQ(e.target.value)}
          onClear={() => s.setBkQ('')}
          inputRef={bkRef}
          placeholder="SEARCH YOUR BACKLOG…"
          w="230px"
          pfs={11}
          ifs={10.5}
          pad="7px 10px"
        />
        {v.hasBkNote ? (
          <span style={{ fontFamily: MONO, fontSize: '9.5px', color: 'var(--gt-faint)', whiteSpace: 'nowrap' }}>{v.bkNote}</span>
        ) : null}
        <FilterBar />
      </div>

      {v.bkNarrowed ? (
        <div style={{ padding: '40px 24px', textAlign: 'center', fontFamily: MONO, fontSize: 11, lineHeight: 2, color: 'var(--gt-muted)' }}>
          {v.bkNarrowTxt}
        </div>
      ) : null}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill,minmax(168px,1fr))',
          gap: 14,
          padding: '16px 24px 30px',
        }}
      >
        {v.bkRows.map((row) => (
          <BacklogTile key={row.key} s={row} />
        ))}
        {v.bkAddTile ? (
          <div
            onClick={() => s.openTrack()}
            className="d-hov-acc-bd"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              border: '1px dashed var(--gt-border-3)',
              minHeight: 200,
              cursor: 'pointer',
            }}
          >
            <span style={{ fontFamily: PIXEL, fontSize: 16, color: 'var(--gt-ghost-2)' }}>+</span>
            <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.08em', color: 'var(--gt-muted-2)', textAlign: 'center', lineHeight: 1.8 }}>
              TRACK A
              <br />
              SOLO GAME
            </span>
          </div>
        ) : null}
      </div>

    </div>
  );
}
